import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  useAdminGetSupplyBeamThemeMutation,
  useAdminSaveSupplyBeamThemeMutation,
} from "../../../features/admin/supplyBeamConfig/supplyBeamConfigApi";
import {
  FiCheck,
  FiRefreshCw,
  FiSave,
  FiSmartphone,
  FiMonitor,
  FiEye,
  FiSliders,
  FiDroplet,
  FiType,
  FiLayers,
  FiLayout,
  FiImage,
  FiBox,
  FiSun,
  FiMoon,
} from "react-icons/fi";
import { MdOutlineColorLens, MdAutoGraph, MdOutlinePalette } from "react-icons/md";

// ─────────────────────────────────────────────────────────────────
// Default Theme Preset Values
// ─────────────────────────────────────────────────────────────────
export const defaultTheme = {
  theme_name: "Modern Indigo",
  theme_mode: "light", // "light" | "dark" | "system"
  primary_color: "#4f46e5",
  secondary_color: "#06b6d4",
  accent_color: "#f59e0b",
  background_color: "#f8fafc",
  card_bg_color: "#ffffff",
  sidebar_bg_color: "#0d233a",
  text_primary_color: "#0f172a",
  text_secondary_color: "#64748b",
  font_family: "Inter, sans-serif",
  font_size_base: "14px",
  border_radius: "10px", // "4px" | "8px" | "10px" | "14px" | "20px"
  header_style: "brand", // "brand" | "dark" | "light" | "glass"
  button_style: "solid", // "solid" | "gradient" | "soft" | "outline"
  shadow_elevation: "subtle", // "none" | "subtle" | "medium" | "elevated"
  logo_url: "",
  app_icon_url: "",
  app_title: "SupplyBeam",
  support_email: "",
  custom_css: "",
};

// ─────────────────────────────────────────────────────────────────
// Curated Presets
// ─────────────────────────────────────────────────────────────────
const THEME_PRESETS = [
  {
    name: "Modern Indigo",
    primary: "#4f46e5",
    secondary: "#06b6d4",
    accent: "#f59e0b",
    bg: "#f8fafc",
    card: "#ffffff",
    text: "#0f172a",
    font: "Inter, sans-serif",
    radius: "10px",
  },
  {
    name: "Emerald Logistics",
    primary: "#059669",
    secondary: "#10b981",
    accent: "#f59e0b",
    bg: "#f0fdf4",
    card: "#ffffff",
    text: "#064e3b",
    font: "Plus Jakarta Sans, sans-serif",
    radius: "8px",
  },
  {
    name: "Enterprise Cobalt",
    primary: "#1d4ed8",
    secondary: "#38bdf8",
    accent: "#f59e0b",
    bg: "#f8fafc",
    card: "#ffffff",
    text: "#1e293b",
    font: "Roboto, sans-serif",
    radius: "6px",
  },
  {
    name: "Crimson Velocity",
    primary: "#dc2626",
    secondary: "#f97316",
    accent: "#eab308",
    bg: "#fef2f2",
    card: "#ffffff",
    text: "#450a0a",
    font: "Outfit, sans-serif",
    radius: "12px",
  },
  {
    name: "Royal Violet",
    primary: "#7c3aed",
    secondary: "#c084fc",
    accent: "#06b6d4",
    bg: "#faf5ff",
    card: "#ffffff",
    text: "#2e1065",
    font: "Poppins, sans-serif",
    radius: "14px",
  },
  {
    name: "Midnight Slate (Dark)",
    primary: "#6366f1",
    secondary: "#38bdf8",
    accent: "#10b981",
    bg: "#0f172a",
    card: "#1e293b",
    text: "#f8fafc",
    font: "Inter, sans-serif",
    radius: "10px",
    mode: "dark",
  },
];

const FONT_OPTIONS = [
  { label: "Inter (Modern Tech & Clean)", value: "Inter, sans-serif" },
  { label: "Plus Jakarta Sans (Premium SaaS)", value: "Plus Jakarta Sans, sans-serif" },
  { label: "Outfit (Geometric & Dynamic)", value: "Outfit, sans-serif" },
  { label: "Roboto (Classic Enterprise)", value: "Roboto, sans-serif" },
  { label: "Poppins (Friendly & Rounded)", value: "Poppins, sans-serif" },
  { label: "Space Grotesk (High-Tech Monospace)", value: "Space Grotesk, sans-serif" },
];

const RADIUS_OPTIONS = [
  { label: "Sharp (4px)", value: "4px" },
  { label: "Compact (8px)", value: "8px" },
  { label: "Modern (10px)", value: "10px" },
  { label: "Soft Curved (14px)", value: "14px" },
  { label: "Pill (20px)", value: "20px" },
];

const BUTTON_STYLES = [
  { label: "Solid Color", value: "solid" },
  { label: "Gradient Glow", value: "gradient" },
  { label: "Soft Tint", value: "soft" },
  { label: "Border Outline", value: "outline" },
];

const HEADER_STYLES = [
  { label: "Brand Accent", value: "brand" },
  { label: "Dark Navy", value: "dark" },
  { label: "Clean White", value: "light" },
  { label: "Glassmorphism", value: "glass" },
];

export const SupplyBeamThemeConfig = ({ tenantInfo }) => {
  const token = sessionStorage.getItem("access_token");
  const tenantId = tenantInfo?.tenant_id;

  const [theme, setTheme] = useState(defaultTheme);
  const [previewDevice, setPreviewDevice] = useState("mobile"); // "mobile" | "desktop"
  const [activeCategory, setActiveCategory] = useState("colors"); // "colors" | "typography" | "layout" | "branding"

  const [fetchTheme, { data: themeData, isLoading: loadingTheme }] =
    useAdminGetSupplyBeamThemeMutation();
  const [saveTheme, { isLoading: savingTheme }] =
    useAdminSaveSupplyBeamThemeMutation();

  // Load existing theme from supply_beam_config table
  useEffect(() => {
    if (!tenantId) return;
    fetchTheme({
      token,
      tenant_id: tenantId,
    });
  }, [tenantId]);

  useEffect(() => {
    if (themeData?.body?.theme) {
      setTheme({ ...defaultTheme, ...themeData.body.theme });
    }
  }, [themeData]);

  const updateField = (field, value) => {
    setTheme((prev) => ({ ...prev, [field]: value }));
  };

  const applyPreset = (preset) => {
    setTheme((prev) => ({
      ...prev,
      theme_name: preset.name,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
      background_color: preset.bg,
      card_bg_color: preset.card,
      text_primary_color: preset.text,
      font_family: preset.font,
      border_radius: preset.radius,
      theme_mode: preset.mode || "light",
    }));
    toast.success(`Preset "${preset.name}" applied!`, { id: "preset" });
  };

  const handleSave = async () => {
    if (!tenantId) {
      toast.error("Please select a tenant client first.");
      return;
    }
    try {
      const res = await saveTheme({
        token,
        tenant_id: tenantId,
        theme: theme,
      }).unwrap();

      if (res.status === 200 || res.success) {
        toast.success("Theme configuration saved in Main SaaS DB successfully!");
      } else {
        toast.error(res.message || "Failed to save theme configuration.");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save theme configuration.");
    }
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all theme settings to system defaults?")) {
      setTheme(defaultTheme);
      toast.success("Reset to default theme values.");
    }
  };

  // Color picker helper component
  const ColorControl = ({ label, field, hint }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        marginBottom: "10px",
      }}
    >
      <div>
        <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b" }}>{label}</div>
        {hint && <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <input
          type="text"
          value={theme[field] || ""}
          onChange={(e) => updateField(field, e.target.value)}
          style={{
            width: "88px",
            padding: "6px 8px",
            border: "1.5px solid #cbd5e1",
            borderRadius: "6px",
            fontSize: "12px",
            fontFamily: "monospace",
            fontWeight: 700,
            color: "#1e293b",
            textAlign: "center",
            textTransform: "uppercase",
          }}
        />
        <div
          style={{
            position: "relative",
            width: "36px",
            height: "36px",
            borderRadius: "8px",
            border: "2px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            background: theme[field] || "#000",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          <input
            type="color"
            value={theme[field] || "#4f46e5"}
            onChange={(e) => updateField(field, e.target.value)}
            style={{
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              cursor: "pointer",
              opacity: 0,
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Top Banner & Quick Presets */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e1b4b, #312e81 60%, #4338ca)",
          borderRadius: "16px",
          padding: "24px 28px",
          color: "#ffffff",
          boxShadow: "0 10px 25px -5px rgba(49, 46, 129, 0.25)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "20px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <span
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "6px",
                borderRadius: "8px",
                display: "inline-flex",
              }}
            >
              <MdOutlinePalette size={20} />
            </span>
            <h2 style={{ fontSize: "20px", fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
              Theme, Appearance & Branding Engine
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: "13.5px", color: "#c7d2fe", maxWidth: "620px", lineHeight: "1.5" }}>
            Configure client-specific branding, colors, typography, and button styles. Stored as a unified
            theme profile row in <code>supply_beam_config</code> and consumable via SupplyBeam mobile & web APIs.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 18px",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiRefreshCw size={14} /> Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={savingTheme}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 24px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              border: "none",
              color: "#fff",
              borderRadius: "10px",
              fontSize: "13.5px",
              fontWeight: 800,
              cursor: savingTheme ? "not-allowed" : "pointer",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
              transition: "all 0.15s ease",
            }}
          >
            <FiSave size={16} /> {savingTheme ? "Saving..." : "Save Theme Configuration"}
          </button>
        </div>
      </div>

      {/* 1-Click Curated Presets Bar */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          padding: "16px 20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
      >
        <div style={{ fontSize: "12.5px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>
          Quick Brand Presets (1-Click Apply)
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {THEME_PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => applyPreset(preset)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 14px",
                borderRadius: "8px",
                border: theme.theme_name === preset.name ? `2px solid ${preset.primary}` : "1px solid #e2e8f0",
                background: theme.theme_name === preset.name ? `${preset.primary}0d` : "#fff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", gap: "3px" }}>
                <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: preset.primary }} />
                <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: preset.secondary }} />
                <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: preset.accent }} />
              </div>
              <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#1e293b" }}>{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid: Form Controls (Left) vs Interactive Real-Time Live Preview (Right) */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "24px", alignItems: "start" }}>
        {/* LEFT COLUMN: Configuration Controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Category Tabs */}
          <div
            style={{
              display: "flex",
              borderBottom: "1.5px solid #e2e8f0",
              background: "#fff",
              borderRadius: "12px 12px 0 0",
              padding: "4px 8px 0 8px",
              gap: "4px",
            }}
          >
            {[
              { id: "colors", label: "Color Palette", icon: <FiDroplet size={15} /> },
              { id: "typography", label: "Typography & Font", icon: <FiType size={15} /> },
              { id: "layout", label: "UI Shapes & Buttons", icon: <FiLayout size={15} /> },
              { id: "branding", label: "Branding & Assets", icon: <FiImage size={15} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategory(tab.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  padding: "11px 16px",
                  border: "none",
                  borderBottom: activeCategory === tab.id ? `3px solid ${theme.primary_color}` : "3px solid transparent",
                  background: "transparent",
                  color: activeCategory === tab.id ? theme.primary_color : "#64748b",
                  fontSize: "13px",
                  fontWeight: activeCategory === tab.id ? 800 : 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "0 0 14px 14px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
          >
            {/* 1. COLOR PALETTE */}
            {activeCategory === "colors" && (
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "14px" }}>
                  Brand & Accent Colors
                </h4>
                <ColorControl
                  label="Primary Brand Color"
                  field="primary_color"
                  hint="Main brand accent, primary CTA buttons, active state indicators"
                />
                <ColorControl
                  label="Secondary Brand Color"
                  field="secondary_color"
                  hint="Sub-accents, scanner crosshair, gradient overlays"
                />
                <ColorControl
                  label="Highlight / Accent Color"
                  field="accent_color"
                  hint="Warning badges, pending picklists, attention highlights"
                />

                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", margin: "24px 0 14px 0" }}>
                  Canvas & Surface Colors
                </h4>
                <ColorControl
                  label="App Canvas Background"
                  field="background_color"
                  hint="Main application screen background canvas"
                />
                <ColorControl
                  label="Surface & Card Background"
                  field="card_bg_color"
                  hint="Cards, floating scanner modal, modal sheets"
                />
                <ColorControl
                  label="Sidebar / App Bar Background"
                  field="sidebar_bg_color"
                  hint="Navigation drawer, top header backdrop"
                />

                <div style={{ marginTop: "18px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "8px" }}>
                    Default Theme Mode
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {[
                      { id: "light", label: "Light Mode", icon: <FiSun size={14} /> },
                      { id: "dark", label: "Dark Mode", icon: <FiMoon size={14} /> },
                      { id: "system", label: "System Auto", icon: <FiSliders size={14} /> },
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => updateField("theme_mode", mode.id)}
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "10px",
                          borderRadius: "8px",
                          border: theme.theme_mode === mode.id ? `2px solid ${theme.primary_color}` : "1px solid #cbd5e1",
                          background: theme.theme_mode === mode.id ? `${theme.primary_color}10` : "#fff",
                          color: theme.theme_mode === mode.id ? theme.primary_color : "#64748b",
                          fontSize: "12.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {mode.icon} {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 2. TYPOGRAPHY */}
            {activeCategory === "typography" && (
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "14px" }}>
                  Typography & Font Family
                </h4>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    Primary Font Family
                  </label>
                  <select
                    value={theme.font_family}
                    onChange={(e) => updateField("font_family", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13.5px",
                      color: "#1e293b",
                      background: "#fff",
                      fontWeight: 600,
                      outline: "none",
                    }}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    Base Font Size (Scale)
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["13px", "14px", "15px", "16px"].map((sz) => (
                      <button
                        key={sz}
                        type="button"
                        onClick={() => updateField("font_size_base", sz)}
                        style={{
                          flex: 1,
                          padding: "9px",
                          borderRadius: "8px",
                          border: theme.font_size_base === sz ? `2px solid ${theme.primary_color}` : "1px solid #cbd5e1",
                          background: theme.font_size_base === sz ? `${theme.primary_color}10` : "#fff",
                          color: theme.font_size_base === sz ? theme.primary_color : "#64748b",
                          fontSize: "13px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {sz} {sz === "14px" && "(Default)"}
                      </button>
                    ))}
                  </div>
                </div>

                <ColorControl
                  label="Primary Text Color"
                  field="text_primary_color"
                  hint="Headings, titles, primary readouts, and QR numbers"
                />
                <ColorControl
                  label="Secondary / Muted Text Color"
                  field="text_secondary_color"
                  hint="Timestamps, subtitles, transporter labels, and captions"
                />
              </div>
            )}

            {/* 3. UI SHAPES & BUTTONS */}
            {activeCategory === "layout" && (
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "14px" }}>
                  Component Curvature & Shapes
                </h4>
                <div style={{ marginBottom: "18px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "8px" }}>
                    Border Radius (Curvature)
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {RADIUS_OPTIONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => updateField("border_radius", r.value)}
                        style={{
                          padding: "10px",
                          borderRadius: r.value,
                          border: theme.border_radius === r.value ? `2px solid ${theme.primary_color}` : "1px solid #cbd5e1",
                          background: theme.border_radius === r.value ? `${theme.primary_color}10` : "#fff",
                          color: theme.border_radius === r.value ? theme.primary_color : "#64748b",
                          fontSize: "12.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                          textAlign: "center",
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", margin: "24px 0 14px 0" }}>
                  Button & Header Styles
                </h4>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "8px" }}>
                    Button Visual Style
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {BUTTON_STYLES.map((b) => (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => updateField("button_style", b.value)}
                        style={{
                          padding: "10px",
                          borderRadius: "8px",
                          border: theme.button_style === b.value ? `2px solid ${theme.primary_color}` : "1px solid #cbd5e1",
                          background: theme.button_style === b.value ? `${theme.primary_color}10` : "#fff",
                          color: theme.button_style === b.value ? theme.primary_color : "#64748b",
                          fontSize: "12.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "8px" }}>
                    Header / App Bar Style
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {HEADER_STYLES.map((h) => (
                      <button
                        key={h.value}
                        type="button"
                        onClick={() => updateField("header_style", h.value)}
                        style={{
                          padding: "10px",
                          borderRadius: "8px",
                          border: theme.header_style === h.value ? `2px solid ${theme.primary_color}` : "1px solid #cbd5e1",
                          background: theme.header_style === h.value ? `${theme.primary_color}10` : "#fff",
                          color: theme.header_style === h.value ? theme.primary_color : "#64748b",
                          fontSize: "12.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {h.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 4. BRANDING & ASSETS */}
            {activeCategory === "branding" && (
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "14px" }}>
                  Brand Identity & Asset Links
                </h4>
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    App Brand Title
                  </label>
                  <input
                    value={theme.app_title || ""}
                    onChange={(e) => updateField("app_title", e.target.value)}
                    placeholder="e.g. Acme SupplyBeam or Bonton Track"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1e293b",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    Brand Logo URL (PNG/SVG)
                  </label>
                  <input
                    value={theme.logo_url || ""}
                    onChange={(e) => updateField("logo_url", e.target.value)}
                    placeholder="https://example.com/logo.png"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1e293b",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    Favicon / App Icon URL
                  </label>
                  <input
                    value={theme.app_icon_url || ""}
                    onChange={(e) => updateField("app_icon_url", e.target.value)}
                    placeholder="https://example.com/favicon.ico"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1e293b",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block", marginBottom: "6px" }}>
                    Support Email / Contact Link
                  </label>
                  <input
                    value={theme.support_email || ""}
                    onChange={(e) => updateField("support_email", e.target.value)}
                    placeholder="support@genefied.in"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1e293b",
                      outline: "none",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Real-Time Interactive Live Preview */}
        <div style={{ position: "sticky", top: "20px" }}>
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "16px 20px",
              boxShadow: "0 10px 25px -5px rgba(0,0,0,0.06)",
            }}
          >
            {/* Header with Switcher */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: "14px",
                borderBottom: "1px solid #f1f5f9",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FiEye size={16} color={theme.primary_color} />
                <span style={{ fontSize: "13.5px", fontWeight: 800, color: "#0f172a" }}>
                  Real-Time Live UI Preview
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  background: "#f1f5f9",
                  borderRadius: "8px",
                  padding: "2px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setPreviewDevice("mobile")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 12px",
                    borderRadius: "6px",
                    border: "none",
                    background: previewDevice === "mobile" ? "#fff" : "transparent",
                    color: previewDevice === "mobile" ? "#0f172a" : "#64748b",
                    fontSize: "11.5px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <FiSmartphone size={13} /> Mobile
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDevice("desktop")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 12px",
                    borderRadius: "6px",
                    border: "none",
                    background: previewDevice === "desktop" ? "#fff" : "transparent",
                    color: previewDevice === "desktop" ? "#0f172a" : "#64748b",
                    fontSize: "11.5px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <FiMonitor size={13} /> Desktop
                </button>
              </div>
            </div>

            {/* PREVIEW CONTAINER */}
            <div
              style={{
                background: theme.background_color || "#f8fafc",
                borderRadius: theme.border_radius || "12px",
                border: "1.5px solid #e2e8f0",
                overflow: "hidden",
                fontFamily: theme.font_family || "Inter, sans-serif",
                transition: "all 0.2s ease",
              }}
            >
              {/* App Bar / Header */}
              <div
                style={{
                  padding: "12px 16px",
                  background:
                    theme.header_style === "brand"
                      ? theme.primary_color
                      : theme.header_style === "dark"
                      ? theme.sidebar_bg_color || "#0d233a"
                      : "#ffffff",
                  color:
                    theme.header_style === "light"
                      ? theme.text_primary_color
                      : "#ffffff",
                  borderBottom: theme.header_style === "light" ? "1px solid #e2e8f0" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "12px",
                    }}
                  >
                    SB
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 800 }}>
                    {theme.app_title || "SupplyBeam"}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: "20px",
                    background: theme.secondary_color,
                    color: "#fff",
                  }}
                >
                  ONLINE
                </span>
              </div>

              {/* Body Content */}
              <div style={{ padding: "18px" }}>
                {/* Surface Card: QR Scanning Mock */}
                <div
                  style={{
                    background: theme.card_bg_color || "#ffffff",
                    borderRadius: theme.border_radius || "10px",
                    padding: "16px",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                    marginBottom: "14px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: theme.text_secondary_color }}>
                      DISPATCH CARTON #4482
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 800,
                        padding: "2px 8px",
                        borderRadius: "6px",
                        background: `${theme.accent_color}18`,
                        color: theme.accent_color,
                      }}
                    >
                      STAGED
                    </span>
                  </div>

                  <div style={{ fontSize: "15px", fontWeight: 800, color: theme.text_primary_color, marginBottom: "4px" }}>
                    Bhiwadi Plant &rarr; Delhi CDC
                  </div>
                  <div style={{ fontSize: "12px", color: theme.text_secondary_color }}>
                    Transporter: Delhivery Express | Vehicle: DL01-AX-9921
                  </div>

                  {/* QR Box Visualizer */}
                  <div
                    style={{
                      margin: "14px 0",
                      padding: "16px",
                      background: `${theme.primary_color}08`,
                      borderRadius: theme.border_radius || "8px",
                      border: `1.5px dashed ${theme.primary_color}`,
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "11.5px", fontWeight: 700, color: theme.primary_color, textTransform: "uppercase" }}>
                      Tap to Trigger Camera QR Scanner
                    </div>
                    <div style={{ fontSize: "11px", color: theme.text_secondary_color, marginTop: "2px" }}>
                      Supports Parent Carton & Child Items
                    </div>
                  </div>

                  {/* Simulated Action Button */}
                  <button
                    type="button"
                    style={{
                      width: "100%",
                      padding: "11px",
                      borderRadius: theme.border_radius || "8px",
                      border: theme.button_style === "outline" ? `2px solid ${theme.primary_color}` : "none",
                      background:
                        theme.button_style === "gradient"
                          ? `linear-gradient(135deg, ${theme.primary_color}, ${theme.secondary_color})`
                          : theme.button_style === "soft"
                          ? `${theme.primary_color}18`
                          : theme.button_style === "outline"
                          ? "transparent"
                          : theme.primary_color,
                      color:
                        theme.button_style === "soft" || theme.button_style === "outline"
                          ? theme.primary_color
                          : "#ffffff",
                      fontSize: "13px",
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow:
                        theme.button_style === "gradient" || theme.button_style === "solid"
                          ? `0 4px 12px ${theme.primary_color}40`
                          : "none",
                    }}
                  >
                    Complete Checkout & Print LR
                  </button>
                </div>

                {/* Sub-Card: Stage Workflow Indicators */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    background: theme.card_bg_color || "#fff",
                    borderRadius: theme.border_radius || "8px",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <span style={{ fontSize: "12px", fontWeight: 700, color: theme.text_secondary_color }}>
                    Geofence Perimeter:
                  </span>
                  <span style={{ fontSize: "12px", fontWeight: 800, color: theme.primary_color }}>
                    500m Enforced
                  </span>
                </div>
              </div>
            </div>

            {/* Note */}
            <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "12px", textAlign: "center", lineHeight: "1.4" }}>
              Changes in this theme profile automatically customize mobile app UI and web panels for this tenant.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplyBeamThemeConfig;
